import { useState, useRef, useEffect } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { getStatusLabel, getPriorityLabel, getStatusLabels, getPriorityLabels } from '../types';
import type {
  TicketStatus,
  TicketPriority,
  Ticket,
  TicketAgent,
  TicketAttachment,
  TimelineItem,
  TicketEventType,
} from '../types';
import {
  ArrowRight, Clock, Plus, X, Pencil,
  UserPlus, UserMinus, Lock, Paperclip, FileText, Archive,
  Download, Trash2, ChevronDown, ChevronUp, Send, Image,
  Move, CheckCircle2, Circle, ListTodo, Play, MessageCircle, List,
  AlertTriangle,
} from 'lucide-react';
import TicketChatView from '../components/TicketChatView';
import { format, formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';
import HierarchyBreadcrumb from '../components/HierarchyBreadcrumb';
import HoursProgressBar from '../components/HoursProgressBar';
import DeadlineBanner from '../components/DeadlineBanner';
import DeadlineBadge from '../components/DeadlineBadge';
import TimerSummaryModal from '../components/TimerSummaryModal';
import ConfirmModal from '../components/ConfirmModal';
import StatusStepper from '../components/StatusStepper';
import VoiceAiBadge from '../components/VoiceAiBadge';
import SlaBadge from '../components/SlaBadge';

// ============================================================
// Utilities
// ============================================================
function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function isImageMime(mime: string): boolean {
  return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(mime);
}

function AttachmentIcon({ mime }: { mime: string }) {
  if (isImageMime(mime)) return <Image size={20} className="text-info-500" />;
  if (mime === 'application/pdf') return <FileText size={20} className="text-danger-500" />;
  if (mime.includes('zip')) return <Archive size={20} className="text-warning-600" />;
  return <FileText size={20} className="text-surface-500" />;
}

function roleLabel(role: string | undefined, t: (key: string) => string) {
  if (!role) return '';
  const key = `userRole.${role}`;
  const label = t(key);
  return label === key ? role : label;
}

function avatarInitials(name?: string) {
  if (!name) return '?';
  const parts = name.split(' ');
  return parts.length >= 2 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
}

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip';

// ============================================================
// AttachmentItem
// ============================================================
function AttachmentItem({
  att,
  canDelete,
  onDelete,
}: {
  att: TicketAttachment;
  canDelete: boolean;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 p-2 bg-surface-50 rounded-lg border border-surface-400/15 group">
      {isImageMime(att.mime_type) ? (
        <a href={att.url} target="_blank" rel="noreferrer" className="flex-shrink-0">
          <img src={att.url} alt={att.original_name}
            className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity" />
        </a>
      ) : (
        <div className="w-10 h-10 flex items-center justify-center bg-surface-0 rounded border border-surface-400/15 flex-shrink-0">
          <AttachmentIcon mime={att.mime_type} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-surface-800 truncate" title={att.original_name}>
          {att.original_name.length > 28 ? att.original_name.slice(0, 25) + '...' : att.original_name}
        </p>
        <p className="text-xs text-surface-400">{fmtBytes(att.file_size_bytes)}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <a href={att.url} download={att.original_name} target="_blank" rel="noreferrer"
          className="p-1 text-surface-400 hover:text-primary-600 transition-colors" title={t('common.download')}>
          <Download size={14} />
        </a>
        {canDelete && (
          <button onClick={() => onDelete(att.id)}
            className="p-1 text-surface-400 hover:text-danger-500 transition-colors" title={t('common.delete')}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Event description helper
// ============================================================
function eventDescription(t: TFunction, eventType: TicketEventType, metadata: Record<string, any>, actorName?: string): string {
  const actor = actorName || t('ticketDetail.unknownActor');
  const defaultAgent = t('admin.roleAgent');
  switch (eventType) {
    case 'ticket_created':
      return t('ticketDetail.openedTicket', { actor });
    case 'status_changed': {
      const from = getStatusLabel(metadata.from as TicketStatus, t) || metadata.from;
      const to = getStatusLabel(metadata.to as TicketStatus, t) || metadata.to;
      return `${actor} ${t('ticketDetail.changedStatus')}${from}${t('ticketDetail.to')}${to}"`;
    }
    case 'priority_changed': {
      const from = getPriorityLabel(metadata.from as TicketPriority, t) || metadata.from;
      const to = getPriorityLabel(metadata.to as TicketPriority, t) || metadata.to;
      return `${actor} ${t('ticketDetail.changedPriority')}${from}${t('ticketDetail.to')}${to}"`;
    }
    case 'agent_assigned':
      return `${metadata.agent_name || defaultAgent} ${t('ticketDetail.assignedBy')}${actor}`;
    case 'agent_removed':
      return `${metadata.agent_name || defaultAgent} ${t('ticketDetail.removedBy')}${actor}`;
    case 'attachment_uploaded':
      return `${actor} ${t('ticketDetail.uploadedFile')}${metadata.original_name || ''}`;
    case 'attachment_deleted':
      return `${actor} ${t('ticketDetail.deletedFile')}${metadata.original_name || ''}`;
    case 'comment_added':
    case 'comment_deleted':
    default:
      return '';
  }
}

function eventDotColor(eventType?: TicketEventType): string {
  switch (eventType) {
    case 'ticket_created':      return 'bg-primary-400';
    case 'status_changed':      return 'bg-warning-400';
    case 'priority_changed':    return 'bg-warning-600';
    case 'agent_assigned':
    case 'agent_removed':       return 'bg-info-500';
    case 'attachment_uploaded':
    case 'attachment_deleted':  return 'bg-surface-400';
    default:                    return 'bg-surface-300';
  }
}

// ============================================================
// TimelineCommentItem
// ============================================================
function TimelineCommentItem({
  item,
  ticketId,
  userId,
  userRole,
  onDeleteComment,
}: {
  item: TimelineItem;
  ticketId: string;
  userId: string;
  userRole: string;
  onDeleteComment: (commentId: string) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(item.content || '');
  const [showAttach, setShowAttach] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const isAdmin = userRole === 'admin';
  const isAuthor = item.actor_id === userId;
  const ageMs = Date.now() - new Date(item.created_at).getTime();
  const canEdit = (isAuthor && ageMs < 60 * 60 * 1000) || isAdmin;
  const canDelete = isAdmin;
  const canAttach = isAuthor || isAdmin || userRole === 'agent';

  const editMutation = useMutation({
    mutationFn: () => api.updateComment(ticketId, item.comment_id!, editContent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      setEditing(false);
    },
  });

  const attachMutation = useMutation({
    mutationFn: (file: File) => api.uploadCommentAttachment(ticketId, item.comment_id!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      setShowAttach(false);
    },
  });

  if (item.is_deleted) {
    return (
      <div className="flex gap-3">
        <div className="w-8 h-8 rounded-full bg-surface-200 flex items-center justify-center flex-shrink-0 mt-1">
          <span className="text-xs text-surface-400">?</span>
        </div>
        <div className="flex-1 text-sm text-surface-400 italic py-2">{t('ticketDetail.commentDeleted')}</div>
      </div>
    );
  }

  const borderClass = item.is_internal
    ? 'border-r-4 border-warning-400 bg-warning-50'
    : 'border-r-4 border-surface-400/30 bg-surface-0';

  return (
    <div className="flex gap-3">
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0 mt-1 text-white text-xs font-bold">
        {avatarInitials(item.actor_name)}
      </div>
      <div className={`flex-1 rounded-xl border border-surface-400/15 p-3 ${borderClass}`}>
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-surface-800">{item.actor_name || t('ticketDetail.user')}</span>
            <span className="text-xs px-2 py-0.5 bg-surface-100 text-surface-500 rounded-full">
              {roleLabel(item.actor_role, t)}
            </span>
            {item.is_internal && (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-warning-100 text-warning-700 rounded-full">
                <Lock size={10} /> {t('common.internal')}
              </span>
            )}
            {item.edited_at && (
              <span className="text-xs text-surface-400">{t('ticketDetail.edited')}</span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs text-surface-400" title={format(new Date(item.created_at), 'dd/MM/yyyy HH:mm', { locale: he })}>
              {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: he })}
            </span>
            {canEdit && !editing && (
              <button onClick={() => { setEditContent(item.content || ''); setEditing(true); }}
                className="p-1 text-surface-300 hover:text-surface-600 transition-colors" title={t('common.edit')}>
                <Pencil size={13} />
              </button>
            )}
            {canAttach && (
              <button onClick={() => setShowAttach(s => !s)}
                className="p-1 text-surface-300 hover:text-surface-600 transition-colors" title={t('ticketDetail.attachFile')}>
                <Paperclip size={13} />
              </button>
            )}
            {canDelete && (
              <button onClick={() => onDeleteComment(item.comment_id!)}
                className="p-1 text-surface-300 hover:text-danger-500 transition-colors" title={t('common.delete')}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Content / Edit */}
        {editing ? (
          <div className="space-y-2">
            <textarea className="input min-h-[80px] text-sm" value={editContent}
              onChange={(e) => setEditContent(e.target.value)} />
            {editMutation.error && <p className="text-xs text-danger-600">{t('ticketDetail.updateError')}</p>}
            <div className="flex gap-2">
              <button onClick={() => editMutation.mutate()} disabled={editMutation.isPending}
                className="btn btn-primary text-xs py-1.5 px-3">
                {editMutation.isPending ? '...' : t('common.save')}
              </button>
              <button onClick={() => setEditing(false)} className="btn bg-surface-100 text-surface-600 text-xs py-1.5 px-3">{t('common.cancel')}</button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-surface-700 whitespace-pre-wrap">{item.content}</p>
        )}

        {/* Attach file area */}
        {showAttach && (
          <div className="mt-2 pt-2 border-t border-surface-400/15">
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) attachMutation.mutate(f); e.target.value = ''; }} />
            <button onClick={() => fileRef.current?.click()} disabled={attachMutation.isPending}
              className="btn bg-surface-100 text-surface-700 text-xs py-1.5 px-3 flex items-center gap-1">
              <Paperclip size={12} /> {attachMutation.isPending ? t('common.uploading') : t('ticketDetail.selectFile')}
            </button>
            {attachMutation.error && <p className="text-xs text-danger-600 mt-1">{t('ticketDetail.uploadError')}</p>}
          </div>
        )}

        {/* Embedded attachments */}
        {item.attachments && item.attachments.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {item.attachments.map((att) => (
              <AttachmentItem key={att.id} att={att} canDelete={false} onDelete={() => {}} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// TicketTimelineFeed
// ============================================================
function TicketTimelineFeed({
  items,
  ticketId,
  userId,
  userRole,
}: {
  items: TimelineItem[];
  ticketId: string;
  userId: string;
  userRole: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = useState<string | null>(null);

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(ticketId, commentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] }),
  });

  if (items.length === 0) {
    return (
      <div className="card text-center py-8 text-surface-500 text-sm">{t('ticketDetail.noActivity')}</div>
    );
  }

  return (
    <div className="relative pr-4">
      {/* Vertical line */}
      <div className="absolute top-0 bottom-0 right-0 w-0.5 bg-surface-200" />

      <div className="space-y-4">
        {items.map((item) => {
          if (item.item_type === 'event') {
            const desc = eventDescription(t, item.event_type!, item.metadata || {}, item.actor_name);
            if (!desc) return null;
            return (
              <div key={item.id} className="flex items-center gap-3">
                <div className={`absolute -right-1.5 w-3 h-3 rounded-full border-2 border-surface-0 ${eventDotColor(item.event_type)}`} />
                <div className="flex items-center gap-2 text-sm text-surface-500 py-1">
                  <span>{desc}</span>
                  <span className="text-xs text-surface-400" title={format(new Date(item.created_at), 'dd/MM/yyyy HH:mm', { locale: he })}>
                    · {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: he })}
                  </span>
                </div>
              </div>
            );
          }

          // comment
          return (
            <div key={item.id} className="relative">
              <div className={`absolute -right-1.5 w-3 h-3 rounded-full border-2 border-surface-0 ${item.is_internal ? 'bg-warning-400' : 'bg-primary-400'}`} />
              <TimelineCommentItem
                item={item}
                ticketId={ticketId}
                userId={userId}
                userRole={userRole}
                onDeleteComment={(cid) => setConfirmDeleteCommentId(cid)}
              />
            </div>
          );
        })}
      </div>

      {deleteCommentMutation.isError && (
        <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm mt-2">{t('ticketDetail.deleteCommentError')}</div>
      )}

      {confirmDeleteCommentId && (
        <ConfirmModal
          title={t('ticketDetail.deleteComment')}
          description={t('ticketDetail.deleteCommentConfirm')}
          confirmLabel={t('common.delete')}
          onConfirm={() => { deleteCommentMutation.mutate(confirmDeleteCommentId); setConfirmDeleteCommentId(null); }}
          onCancel={() => setConfirmDeleteCommentId(null)}
          danger
        />
      )}
    </div>
  );
}

// ============================================================
// CommentComposer
// ============================================================
function CommentComposer({
  ticketId,
  canMarkInternal,
  onSuccess,
}: {
  ticketId: string;
  canMarkInternal: boolean;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files).filter((f) => f.size <= 10 * 1024 * 1024);
    setPendingFiles((prev) => [...prev, ...arr]);
  };

  const submit = async () => {
    if (!content.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const commentRes = await api.createComment(ticketId, { content: content.trim(), is_internal: isInternal });
      const commentId = commentRes.data?.id;
      if (commentId && pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          try {
            await api.uploadCommentAttachment(ticketId, commentId, file);
          } catch {
            setError(t('ticketDetail.commentSavedFileError', { name: file.name }));
          }
        }
      }
      setContent('');
      setPendingFiles([]);
      setIsInternal(false);
      onSuccess();
    } catch {
      setError(t('ticketDetail.commentError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-surface-700 mb-3">{t('ticketDetail.addComment')}</h3>
      <textarea
        className="input min-h-[100px] text-sm"
        placeholder={t('ticketDetail.commentPlaceholder')}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}
      />

      {/* Pending files */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {pendingFiles.map((f, i) => (
            <div key={i} className="flex items-center gap-1 px-2 py-1 bg-surface-100 rounded-lg text-xs text-surface-700">
              <Paperclip size={12} />
              <span className="max-w-[120px] truncate">{f.name}</span>
              <button onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                className="text-surface-400 hover:text-danger-500"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-danger-600 mt-1">{error}</p>}

      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 text-xs text-surface-500 hover:text-primary-600 transition-colors">
            <Paperclip size={14} /> {t('tickets.addFile')}
          </button>
          <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          {canMarkInternal && (
            <label className="flex items-center gap-1.5 text-xs text-surface-600 cursor-pointer">
              <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)}
                className="w-3.5 h-3.5 accent-amber-500" />
              <Lock size={12} className="text-warning-500" />
              {t('ticketDetail.internalOnly')}
            </label>
          )}
        </div>
        <button onClick={submit} disabled={submitting || !content.trim()}
          className="btn btn-primary flex items-center gap-2 text-sm py-2 disabled:opacity-50">
          <Send size={14} /> {submitting ? t('common.sending') : t('common.send')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// TicketAttachmentsCard
// ============================================================
function TicketAttachmentsCard({
  attachments,
  ticketId,
  userId,
  userRole,
  onSuccess,
}: {
  attachments: TicketAttachment[];
  ticketId: string;
  userId: string;
  userRole: string;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (attId: string) => api.deleteAttachment(ticketId, attId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] }),
  });

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      await api.uploadTicketAttachment(ticketId, file, setProgress);
      onSuccess();
    } catch {
      // error shown implicitly
    } finally {
      setUploading(false);
      setProgress(0);
      e.target.value = '';
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-surface-700">{t('ticketDetail.attachments')}</h3>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 transition-colors">
          <Plus size={14} /> {uploading ? `${t('common.uploading')} ${progress}%` : t('ticketDetail.uploadFile')}
        </button>
        <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={handleFile} />
      </div>
      {deleteMutation.isError && (
        <div className="bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded-lg text-xs mb-2">{t('ticketDetail.deleteAttachmentError')}</div>
      )}
      {attachments.length === 0 ? (
        <p className="text-xs text-surface-400">{t('ticketDetail.noAttachments')}</p>
      ) : (
        <div className="space-y-2">
          {attachments.map((att) => (
            <AttachmentItem
              key={att.id}
              att={att}
              canDelete={att.uploader_id === userId || userRole === 'admin'}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// WorkLogsCard (collapsible)
// ============================================================
function WorkLogsCard({ workLogs, canAddLog, ticketId, onAddSuccess }: {
  workLogs: any[];
  canAddLog: boolean;
  ticketId: string;
  onAddSuccess: () => void;
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const totalMinutes = workLogs.reduce((s, wl) => s + (wl.duration_minutes || 0), 0);

  return (
    <div className="card">
      <button onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full text-right">
        <span className="text-sm font-semibold text-surface-700 tabular-nums font-display">
          {t('ticketDetail.workLogs')} ({workLogs.length}) · {(totalMinutes / 60).toFixed(1)} {t('common.hoursShort')}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {canAddLog && (
            <button onClick={() => setShowAdd(true)}
              className="btn btn-primary flex items-center gap-1 text-xs py-1.5 w-full justify-center">
              <Plus size={14} /> {t('ticketDetail.newWorkLog')}
            </button>
          )}
          {showAdd && (
            <AddWorkLogModal ticketId={ticketId} onClose={() => setShowAdd(false)} onSuccess={() => { onAddSuccess(); setShowAdd(false); }} />
          )}
          {workLogs.length === 0 ? (
            <p className="text-xs text-surface-400 text-center py-2">{t('ticketDetail.noWorkLogs')}</p>
          ) : (
            workLogs.map((log) => (
              <div key={log.id} className="p-2 bg-surface-50 rounded-lg border border-surface-400/10">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-medium text-surface-700">{log.agent_name}</span>
                  <div className="flex items-center gap-1">
                    {log.requires_review && !log.reviewed && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-warning-100 text-warning-700">
                        {t('workLogs.pendingReview')}
                      </span>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${log.billable ? 'bg-info-100 text-info-700' : 'bg-surface-100 text-surface-500'}`}>
                      {log.billable ? t('common.billable') : t('common.nonBillable')}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-surface-500 mt-1 line-clamp-2">{log.summary}</p>
                <div className="flex items-center gap-2 text-xs text-surface-400 mt-1 flex-wrap">
                  <Clock size={10} />
                  <span className="tabular-nums font-display">{log.duration_minutes}</span> {t('common.minutes')}
                  {(isAdmin || isAgent) && log.start_time && (
                    <> · {format(new Date(log.start_time), 'dd/MM HH:mm', { locale: he })}</>
                  )}
                  {log.source === 'voice_ai' && <VoiceAiBadge confidence={log.ai_confidence} />}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MoveTicketModal
// ============================================================
function MoveTicketModal({ ticketId, currentProjectId, currentMilestoneId, onClose, onSuccess }: {
  ticketId: string;
  currentProjectId?: string;
  currentMilestoneId?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [projectId, setProjectId] = useState(currentProjectId || '');
  const [milestoneId, setMilestoneId] = useState(currentMilestoneId || '');
  const [hoursAction, setHoursAction] = useState<'move' | 'keep' | 'split'>('move');

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.getProjects({ limit: 100 }),
  });

  const { data: milestonesData } = useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => api.getMilestones(projectId),
    enabled: !!projectId,
  });

  const projects = projectsData?.data ?? [];
  const milestones = milestonesData?.data ?? [];

  const mutation = useMutation({
    mutationFn: () => api.moveTicket(ticketId, {
      project_id: projectId || null,
      milestone_id: milestoneId || null,
      hours_action: hoursAction,
    }),
    onSuccess,
  });

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-surface-900">{t('tickets.moveTicket')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="label">{t('tickets.project')}</label>
            <select className="input" value={projectId} onChange={(e) => { setProjectId(e.target.value); setMilestoneId(''); }}>
              <option value="">{t('tickets.noProject')}</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {projectId && milestones.length > 0 && (
            <div>
              <label className="label">{t('tickets.milestone')}</label>
              <select className="input" value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)}>
                <option value="">{t('tickets.noMilestone')}</option>
                {milestones.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">{t('tickets.hoursHandling')}</label>
            <div className="space-y-2">
              {[
                { value: 'move' as const, label: t('tickets.moveHours') },
                { value: 'keep' as const, label: t('tickets.keepHours') },
                { value: 'split' as const, label: t('tickets.splitHours') },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="hours_action" value={opt.value}
                    checked={hoursAction === opt.value} onChange={() => setHoursAction(opt.value)}
                    className="w-4 h-4 accent-primary-600" />
                  <span className="text-sm text-surface-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {mutation.error && <p className="text-sm text-danger-600">{t('tickets.moveError')}</p>}

          <div className="flex gap-3">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
              {mutation.isPending ? t('tickets.moving') : t('tickets.move')}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// SubtasksSection
// ============================================================
function SubtasksSection({ ticketId, isParent }: { ticketId: string; isParent: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');

  const { data: subtasksData } = useQuery({
    queryKey: ['subtasks', ticketId],
    queryFn: () => api.getSubtasks(ticketId),
    enabled: isParent,
  });

  const createMutation = useMutation({
    mutationFn: () => api.createSubtask(ticketId, {
      title,
      estimated_hours: estimatedHours ? parseFloat(estimatedHours) : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', ticketId] });
      setTitle('');
      setEstimatedHours('');
      setShowAdd(false);
    },
  });

  const subtasks = subtasksData?.data ?? [];
  if (!isParent) return null;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-surface-700 flex items-center gap-2">
          <ListTodo size={16} /> {t('tickets.subtasks')} ({subtasks.length})
        </h3>
        <button onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 transition-colors">
          <Plus size={14} /> {t('tickets.addSubtask')}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }}
          className="flex items-center gap-2 mb-3 p-2 bg-surface-50 rounded-lg">
          <input className="input flex-1 text-sm" placeholder={t('tickets.subtaskTitle')} value={title}
            onChange={(e) => setTitle(e.target.value)} required minLength={3} />
          <input type="number" step="0.5" className="input w-20 text-sm" placeholder={t('common.hours')}
            value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)} />
          <button type="submit" disabled={createMutation.isPending}
            className="btn btn-primary text-xs py-1.5 px-3">
            {createMutation.isPending ? '...' : t('common.create')}
          </button>
          <button type="button" onClick={() => setShowAdd(false)} className="text-surface-400 hover:text-surface-600">
            <X size={16} />
          </button>
        </form>
      )}

      {subtasks.length === 0 ? (
        <p className="text-xs text-surface-400 text-center py-2">{t('tickets.noSubtasks')}</p>
      ) : (
        <div className="space-y-1.5">
          {subtasks.map((st: Ticket) => (
            <Link key={st.id} to={`/tickets/${st.id}`}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-surface-50 transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                {st.status === 'closed' ? (
                  <CheckCircle2 size={14} className="text-success-500 flex-shrink-0" />
                ) : (
                  <Circle size={14} className="text-surface-400 flex-shrink-0" />
                )}
                <span className={`text-sm truncate ${st.status === 'closed' ? 'text-surface-400 line-through' : 'text-surface-700'}`}>
                  {st.title}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-surface-400 flex-shrink-0">
                {st.estimated_hours != null && (
                  <span className="tabular-nums font-display">{st.actual_hours}/{st.estimated_hours} {t('common.hoursShort')}</span>
                )}
                <span className={`px-1.5 py-0.5 rounded-full ${
                  st.status === 'open' ? 'bg-info-100 text-info-700' :
                  st.status === 'in_progress' ? 'bg-warning-100 text-warning-700' :
                  st.status === 'waiting' ? 'bg-orange-100 text-orange-700' :
                  'bg-success-100 text-success-700'
                }`}>
                  {getStatusLabel(st.status, t)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main Page
// ============================================================
export default function TicketDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [editing, setEditing] = useState(false);
  const [showAddAgent, setShowAddAgent] = useState(false);

  const { data: ticketData, isLoading: ticketLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.getTicket(id!),
    enabled: !!id,
  });

  const { data: timelineData, isLoading: timelineLoading } = useQuery({
    queryKey: ['ticket-timeline', id],
    queryFn: () => api.getTicketTimeline(id!),
    enabled: !!id,
  });

  const { data: workLogsData } = useQuery({
    queryKey: ['work-logs', { ticket_id: id }],
    queryFn: () => api.getWorkLogs({ ticket_id: id, limit: 100 }),
    enabled: !!id,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers({ limit: 100 }),
    enabled: !!user && (user.role === 'admin' || user.role === 'agent'),
  });

  const addAgentMutation = useMutation({
    mutationFn: (agentId: string) => api.addTicketAgent(id!, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
      setShowAddAgent(false);
    },
  });

  const removeAgentMutation = useMutation({
    mutationFn: (agentId: string) => api.removeTicketAgent(id!, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
    },
  });

  const ticket = ticketData?.data;
  const timeline = timelineData?.data?.timeline ?? [];
  const ticketAttachments = timelineData?.data?.ticket_attachments ?? [];
  const workLogs = workLogsData?.data ?? [];
  const allUsers = usersData?.data ?? [];
  const agents = allUsers.filter((u) => u.role === 'agent' || u.role === 'admin');
  const assignedAgents: TicketAgent[] = ticket?.agents ?? [];

  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const isCustomerRole = user && ['customer_manager', 'customer_viewer'].includes(user.role);
  const canEdit = user && ['admin', 'agent', 'customer_manager'].includes(user.role);
  const canAddLog = user && ['admin', 'agent'].includes(user.role);
  const canSeeInternal = isAdmin || isAgent;
  const defaultChatView = !!isCustomerRole;
  const [chatView, setChatView] = useState(defaultChatView);
  const [chatViewInitialized, setChatViewInitialized] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showTimerSummary, setShowTimerSummary] = useState(false);
  const [timerStopData, setTimerStopData] = useState<any>(null);

  useEffect(() => {
    if (ticket && !chatViewInitialized) {
      if (ticket.task_type === 'discussion') {
        setChatView(true);
      }
      setChatViewInitialized(true);
    }
  }, [ticket, chatViewInitialized]);

  const startTimerMutation = useMutation({
    mutationFn: () => api.startTimer(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timer-status'] });
    },
  });

  const statusStepperMutation = useMutation({
    mutationFn: (newStatus: string) => api.updateTicketStatus(id!, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
    },
  });

  if (ticketLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-surface-200 rounded-lg" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="h-64 bg-surface-100 rounded-xl" />
            <div className="h-40 bg-surface-100 rounded-xl" />
          </div>
          <div className="space-y-4">
            <div className="h-48 bg-surface-100 rounded-xl" />
            <div className="h-32 bg-surface-100 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }
  if (!ticket) {
    return (
      <div className="empty-state-illustrated">
        <div className="empty-icon-wrapper">
          <AlertTriangle className="empty-icon" />
        </div>
        <p className="empty-title">{t('tickets.notFound')}</p>
        <p className="empty-text">{t('nav.tickets')}</p>
        <Link to="/tickets" className="btn btn-secondary mt-4 inline-block">{t('tickets.backToTickets')}</Link>
      </div>
    );
  }

  const isParentTicket = !ticket.parent_ticket_id;

  const breadcrumbItems = [
    ...(ticket.organization_name ? [{ label: ticket.organization_name, href: `/organizations/${ticket.organization_id}` }] : []),
    ...(ticket.project_name && ticket.project_id ? [{ label: ticket.project_name, href: `/projects/${ticket.project_id}` }] : []),
    ...(ticket.milestone_name && ticket.milestone_id ? [{ label: ticket.milestone_name, href: `/milestones/${ticket.milestone_id}` }] : []),
    { label: ticket.title },
  ];

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <HierarchyBreadcrumb items={breadcrumbItems} />

      {/* Deadline banner */}
      <DeadlineBanner
        dueDate={ticket.effective_due_date || ticket.due_date}
        source={ticket.due_date_source}
        entityLabel={t('deadline.theTicket')}
      />

      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/tickets" className="p-2 hover:bg-surface-100 rounded-lg transition-colors">
          <ArrowRight size={20} />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="page-title truncate">{ticket.ticket_number ? <span className="text-sm text-surface-400 me-2">TKT-{ticket.ticket_number}</span> : null}{ticket.title}</h1>
          <p className="text-sm text-surface-500">
            {t('tickets.openedAt')}{format(new Date(ticket.opened_at), 'dd/MM/yyyy HH:mm', { locale: he })}
            {ticket.task_type === 'task' && <span className="mr-2 px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full text-xs">{t('tickets.task')}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(isAdmin || isAgent) && (
            <button onClick={() => startTimerMutation.mutate()}
              disabled={startTimerMutation.isPending}
              className="btn bg-success-600 text-white hover:bg-success-700 flex items-center gap-1 text-sm py-1.5">
              <Play size={14} /> {t('tickets.startWork')}
            </button>
          )}
          {(isAdmin || isAgent) && (
            <button onClick={() => setShowMoveModal(true)}
              className="btn btn-secondary flex items-center gap-1 text-sm py-1.5">
              <Move size={14} /> {t('tickets.move')}
            </button>
          )}
        </div>
      </div>

      {/* Status stepper + SLA */}
      {canEdit && !editing && (
        <div className="card py-4 px-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <StatusStepper
              currentStatus={ticket.status}
              onStatusChange={(s) => statusStepperMutation.mutate(s)}
              disabled={statusStepperMutation.isPending}
            />
            <SlaBadge
              responseDueAt={ticket.sla_response_due_at}
              resolutionDueAt={ticket.sla_resolution_due_at}
              responseBreached={ticket.sla_response_breached}
              resolutionBreached={ticket.sla_resolution_breached}
              firstResponseAt={ticket.first_response_at}
              status={ticket.status}
            />
          </div>
          {statusStepperMutation.isError && (
            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded-lg text-xs mt-2">{t('ticketDetail.statusChangeError')}</div>
          )}
        </div>
      )}
      {!canEdit && (ticket.sla_response_due_at || ticket.sla_resolution_due_at) && (
        <div className="card py-3 px-6">
          <SlaBadge
            responseDueAt={ticket.sla_response_due_at}
            resolutionDueAt={ticket.sla_resolution_due_at}
            responseBreached={ticket.sla_response_breached}
            resolutionBreached={ticket.sla_resolution_breached}
            firstResponseAt={ticket.first_response_at}
            status={ticket.status}
          />
        </div>
      )}

      {editing ? (
        <EditTicketForm
          ticket={ticket}
          agents={agents}
          onClose={() => setEditing(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['ticket', id] });
            queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
            setEditing(false);
          }}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

          {/* ── LEFT COLUMN: Timeline / Chat + Composer ── (order-last puts it on right in RTL) */}
          <div className="lg:col-span-2 space-y-4 lg:order-last">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-surface-800">
                {chatView ? t('chat.chatView') : t('tickets.history')}
              </h2>
              <div className="flex items-center gap-2">
                {timelineLoading && <div className="spinner w-4 h-4" />}
                {!isCustomerRole && (
                  <button
                    onClick={() => setChatView((v) => !v)}
                    className="flex items-center gap-1 text-xs text-surface-500 hover:text-primary-600 transition-colors px-2 py-1 rounded-lg hover:bg-surface-100"
                  >
                    {chatView ? <><List size={14} /> {t('chat.timelineView')}</> : <><MessageCircle size={14} /> {t('chat.chatView')}</>}
                  </button>
                )}
              </div>
            </div>
            {chatView ? (
              <TicketChatView timeline={timeline} ticketId={id!} />
            ) : (
              <>
                <TicketTimelineFeed
                  items={timeline}
                  ticketId={id!}
                  userId={user?.id || ''}
                  userRole={user?.role || ''}
                />
                <CommentComposer
                  ticketId={id!}
                  canMarkInternal={canSeeInternal}
                  onSuccess={() => queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] })}
                />
              </>
            )}
          </div>

          {/* ── RIGHT COLUMN: Metadata sidebar ── */}
          <div className="space-y-4 lg:sticky lg:top-24">

            {/* Ticket meta */}
            <div className="card">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-base font-bold text-surface-800">{t('tickets.details')}</h2>
                {canEdit && (
                  <button onClick={() => setEditing(true)}
                    className="btn btn-secondary flex items-center gap-1 text-sm py-1.5">
                    <Pencil size={14} /> {t('common.edit')}
                  </button>
                )}
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-surface-500">{t('common.status')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    ticket.status === 'open' ? 'bg-info-100 text-blue-800' :
                    ticket.status === 'in_progress' ? 'bg-warning-100 text-yellow-800' :
                    ticket.status === 'waiting' ? 'bg-orange-100 text-orange-800' :
                    'bg-success-100 text-green-800'}`}>
                    {getStatusLabel(ticket.status, t)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-surface-500">{t('common.priority')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    ticket.priority === 'urgent' ? 'bg-danger-100 text-red-800' :
                    ticket.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                    ticket.priority === 'medium' ? 'bg-warning-100 text-yellow-800' :
                    'bg-surface-100 text-surface-600'}`}>
                    {getPriorityLabel(ticket.priority, t)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-surface-500">{t('tickets.creator')}</span>
                  <span className="font-medium text-surface-800">{ticket.customer_name || t('common.notSpecified')}</span>
                </div>
                {ticket.description && (
                  <div className="pt-2 border-t border-surface-400/10">
                    <p className="text-surface-500 text-xs mb-1">{t('common.description')}</p>
                    <p className="text-surface-700 text-sm">{ticket.description}</p>
                  </div>
                )}
                {(ticket.effective_due_date || ticket.due_date) && (
                  <div className="flex items-center justify-between pt-2 border-t border-surface-400/10">
                    <span className="text-surface-500">{t('tickets.dueDate')}</span>
                    <DeadlineBadge
                      dueDate={ticket.effective_due_date || ticket.due_date}
                      source={ticket.due_date_source}
                      size="md"
                    />
                  </div>
                )}
                {ticket.closed_at && (
                  <div className="flex items-center justify-between pt-2 border-t border-surface-400/10">
                    <span className="text-surface-500">{t('tickets.closedAt')}</span>
                    <span className="text-surface-700">{format(new Date(ticket.closed_at), 'dd/MM/yyyy HH:mm', { locale: he })}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Assigned agents */}
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-surface-700">{t('tickets.assignedAgents')}</h3>
                {canEdit && (
                  <button onClick={() => setShowAddAgent(true)}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 transition-colors">
                    <UserPlus size={14} /> {t('tickets.addAgent')}
                  </button>
                )}
              </div>
              {showAddAgent && (
                <div className="mb-3 p-3 bg-surface-50 rounded-lg border">
                  <p className="text-xs text-surface-600 mb-2">{t('tickets.selectAgent')}</p>
                  <div className="flex flex-wrap gap-2">
                    {agents.filter((a) => !assignedAgents.some((aa) => aa.id === a.id)).map((a) => (
                      <button key={a.id} onClick={() => addAgentMutation.mutate(a.id)}
                        disabled={addAgentMutation.isPending}
                        className="px-2 py-1 bg-surface-0 border border-surface-400/15 rounded text-xs hover:border-primary-400 hover:bg-primary-50 transition-colors">
                        {a.name}
                      </button>
                    ))}
                    {agents.filter((a) => !assignedAgents.some((aa) => aa.id === a.id)).length === 0 && (
                      <p className="text-xs text-surface-400">{t('tickets.allAgentsAssigned')}</p>
                    )}
                  </div>
                  <button onClick={() => setShowAddAgent(false)} className="mt-2 text-xs text-surface-400 hover:text-surface-600">{t('common.cancel')}</button>
                </div>
              )}
              {addAgentMutation.isError && (
                <div className="bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded-lg text-xs mb-2">{t('ticketDetail.addAgentError')}</div>
              )}
              {removeAgentMutation.isError && (
                <div className="bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded-lg text-xs mb-2">{t('ticketDetail.removeAgentError')}</div>
              )}
              {assignedAgents.length > 0 ? (
                <div className="space-y-2">
                  {assignedAgents.map((agent) => (
                    <div key={agent.id} className="flex items-center justify-between px-2 py-1.5 bg-primary-50 border border-primary-100 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-primary-800">{agent.name}</span>
                        <span className="text-xs text-primary-400 mr-2">({agent.role === 'admin' ? t('admin.roleAdmin') : t('admin.roleAgent')})</span>
                      </div>
                      {canEdit && (
                        <button onClick={() => removeAgentMutation.mutate(agent.id)}
                          disabled={removeAgentMutation.isPending}
                          className="p-1 text-primary-300 hover:text-danger-500 transition-colors">
                          <UserMinus size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-surface-400">{t('tickets.noAgentsAssigned')}</p>
              )}
            </div>

            {/* Time summary */}
            <div className="card">
              <h3 className="text-sm font-semibold text-surface-700 mb-3">{t('tickets.timeSummary')}</h3>
              <div className="space-y-2 text-sm">
                {(() => {
                  const total = workLogs.reduce((s, wl) => s + (wl.duration_minutes || 0), 0);
                  const billable = workLogs.filter(wl => wl.billable).reduce((s, wl) => s + (wl.duration_minutes || 0), 0);
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-surface-500">{t('common.total')}</span>
                        <span className="font-medium tabular-nums font-display">{(total / 60).toFixed(1)} {t('common.hoursShort')} ({total} {t('common.minutesShort')})</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-surface-500">{t('common.billable')}</span>
                        <span className="font-medium text-success-700 tabular-nums font-display">{(billable / 60).toFixed(1)} {t('common.hoursShort')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-surface-500">{t('common.records')}</span>
                        <span className="font-medium tabular-nums font-display">{workLogs.length}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Estimated vs Actual hours */}
            {(ticket.estimated_hours != null || Number(ticket.actual_hours) > 0) && (
              <div className="card tabular-nums font-display">
                <h3 className="text-sm font-semibold text-surface-700 mb-3">{t('tickets.estimatedVsActual')}</h3>
                {ticket.estimated_hours != null && Number(ticket.estimated_hours) > 0 ? (
                  <HoursProgressBar actual={Number(ticket.actual_hours)} estimated={Number(ticket.estimated_hours)} />
                ) : (
                  <p className="text-sm text-surface-600">{Number(ticket.actual_hours).toFixed(1)} {t('tickets.actualWithoutEstimate')}</p>
                )}
              </div>
            )}

            {/* Customer summary */}
            {ticket.customer_summary && (isCustomerRole || isAdmin) && (
              <div className="card">
                <h3 className="text-sm font-semibold text-surface-700 mb-2">{t('tickets.customerSummary')}</h3>
                <p className="text-sm text-surface-600 whitespace-pre-wrap">{ticket.customer_summary}</p>
              </div>
            )}

            {/* Sub-tasks */}
            {isParentTicket && user?.role !== 'customer_viewer' && (
              <SubtasksSection ticketId={id!} isParent={isParentTicket} />
            )}

            {/* Work logs */}
            {canSeeInternal && (
              <WorkLogsCard
                workLogs={workLogs}
                canAddLog={!!canAddLog}
                ticketId={id!}
                onAddSuccess={() => queryClient.invalidateQueries({ queryKey: ['work-logs', { ticket_id: id }] })}
              />
            )}

            {/* Ticket-level attachments */}
            <TicketAttachmentsCard
              attachments={ticketAttachments}
              ticketId={id!}
              userId={user?.id || ''}
              userRole={user?.role || ''}
              onSuccess={() => queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] })}
            />
          </div>
        </div>
      )}

      {/* Move ticket modal */}
      {showMoveModal && (
        <MoveTicketModal
          ticketId={id!}
          currentProjectId={ticket.project_id}
          currentMilestoneId={ticket.milestone_id}
          onClose={() => setShowMoveModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['ticket', id] });
            setShowMoveModal(false);
          }}
        />
      )}

      {/* Timer summary modal */}
      {showTimerSummary && timerStopData && (
        <TimerSummaryModal
          ticketId={timerStopData.ticket_id}
          ticketTitle={timerStopData.ticket_title}
          startTime={timerStopData.start_time}
          endTime={timerStopData.end_time}
          durationMinutes={timerStopData.duration_minutes}
          onClose={() => { setShowTimerSummary(false); setTimerStopData(null); }}
          onSuccess={() => {
            setShowTimerSummary(false);
            setTimerStopData(null);
            queryClient.invalidateQueries({ queryKey: ['work-logs', { ticket_id: id }] });
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// EditTicketForm
// ============================================================
function EditTicketForm({ ticket, agents, onClose, onSuccess }: {
  ticket: Ticket;
  agents: any[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description || '');
  const [priority, setPriority] = useState(ticket.priority);
  const [status, setStatus] = useState(ticket.status);
  const [agentId, setAgentId] = useState(ticket.assigned_agent_id || '');
  const [estimatedHours, setEstimatedHours] = useState(ticket.estimated_hours?.toString() || '');
  const [customerSummary, setCustomerSummary] = useState(ticket.customer_summary || '');
  const [dueDate, setDueDate] = useState(ticket.due_date ? ticket.due_date.slice(0, 10) : '');

  const mutation = useMutation({
    mutationFn: () => api.updateTicket(ticket.id, {
      title, description, priority, status,
      assigned_agent_id: agentId || null,
      estimated_hours: estimatedHours ? parseFloat(estimatedHours) : undefined,
      customer_summary: customerSummary || undefined,
      due_date: dueDate || null,
    } as any),
    onSuccess,
  });

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-surface-900">{t('tickets.editTicket')}</h2>
        <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
        <div>
          <label className="label">{t('common.title')}</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} />
        </div>
        <div>
          <label className="label">{t('common.description')}</label>
          <textarea className="input min-h-[80px]" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">{t('common.status')}</label>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value as TicketStatus)}>
              {Object.entries(getStatusLabels(t)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t('common.priority')}</label>
            <select className="input" value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
              {Object.entries(getPriorityLabels(t)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t('admin.roleAgent')}</label>
            <select className="input" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              <option value="">{t('common.unassigned')}</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">{t('tickets.estimatedHours')}</label>
            <input type="number" step="0.5" className="input" value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value)} placeholder={t('tickets.noEstimate')} />
          </div>
          <div>
            <label className="label">{t('tickets.dueDate')}</label>
            <input type="date" className="input" value={dueDate}
              onChange={(e) => setDueDate(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">{t('tickets.customerSummary')}</label>
          <textarea className="input min-h-[60px]" value={customerSummary}
            onChange={(e) => setCustomerSummary(e.target.value)} rows={2}
            placeholder={t('tickets.customerSummaryPlaceholder')} />
        </div>
        {mutation.error && (
          <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('ticketDetail.updateError')}</div>
        )}
        <div className="flex gap-3">
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
            {mutation.isPending ? t('common.updating') : t('tickets.updateTicket')}
          </button>
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// AddWorkLogModal
// ============================================================
function AddWorkLogModal({ ticketId, onClose, onSuccess }: {
  ticketId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [billable, setBillable] = useState(true);

  const mutation = useMutation({
    mutationFn: () => api.createWorkLog({
      ticket_id: ticketId,
      start_time: new Date(startTime).toISOString(),
      end_time: new Date(endTime).toISOString(),
      summary, billable,
    }),
    onSuccess,
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('ticketDetail.newWorkLogTitle')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('ticketDetail.startTime')}</label>
              <input type="datetime-local" className="input" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </div>
            <div>
              <label className="label">{t('ticketDetail.endTime')}</label>
              <input type="datetime-local" className="input" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </div>
          </div>
          <div>
            <label className="label">{t('ticketDetail.summary')}</label>
            <textarea className="input min-h-[100px]" value={summary} onChange={(e) => setSummary(e.target.value)} required minLength={5} rows={4} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="add-billable" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="w-4 h-4" />
            <label htmlFor="add-billable" className="text-sm text-surface-700">{t('common.billable')}</label>
          </div>
          {mutation.error && (
            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('ticketDetail.createWorkLogError')}</div>
          )}
          <div className="flex gap-3">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
              {mutation.isPending ? t('common.creating') : t('ticketDetail.createWorkLog')}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
